<div mat-dialog-title>
  <div class="subtitle">{{ subtitle }}</div>
  <h2>{{ title }}</h2>
</div>

<mat-dialog-content [formGroup]="form">
  <div class="columns">
    <div class="column">
      <section>
        <h3>S'applique à</h3>
        @if (isGeneral) {
          <p class="hint">
            La ligne générale vaut pour tous les contrats : toutes sociétés, tous types de contrat.
          </p>
        } @else {
          <div class="criteria" formGroupName="criteria">
            <mat-form-field>
              <mat-label>Société</mat-label>
              <mat-select formControlName="companyId">
                <mat-option [value]="null">Toutes</mat-option>
                @for (company of companies(); track company.id) {
                  <mat-option [value]="company.id">{{ company.name }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field>
              <mat-label>Type de contrat</mat-label>
              <mat-select formControlName="contractType">
                <mat-option [value]="null">Tous</mat-option>
                @for (contractType of contractTypes; track contractType) {
                  <mat-option [value]="contractType">{{ contractType }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            @if (setting.precision === 'VALIDATION_TYPE') {
              <mat-form-field>
                <mat-label>Échelon</mat-label>
                <mat-select formControlName="validationType">
                  <mat-option [value]="null">Tous</mat-option>
                  @for (validationType of validationTypes; track validationType) {
                    <mat-option [value]="validationType">{{
                      validationType | validationRequestTypeLabel
                    }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }
            @if (setting.precision === 'DOCUMENT_TAG') {
              <mat-form-field>
                <mat-label>Document</mat-label>
                <mat-select formControlName="documentTag">
                  <mat-option [value]="null">Tous</mat-option>
                  @for (tag of documentTags; track tag) {
                    <mat-option [value]="tag">{{ documentTagLabels[tag] }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }
          </div>
          @if (form.controls.criteria.hasError('noCriterion')) {
            <p class="criteria-error">Une ligne particulière précise au moins un critère.</p>
          }
        }
      </section>

      <section>
        <h3>Destinataires</h3>
        <div class="hint">Profils — et pour chacun, la portée</div>
        <div formGroupName="profiles">
          @for (role of designableRoles; track role) {
            <div class="profile" [formGroupName]="role">
              <mat-checkbox formControlName="selected">{{ roleLabel(role) }}</mat-checkbox>
              @if (form.controls.profiles.controls[role].controls.selected.value) {
                <mat-button-toggle-group
                  formControlName="scope"
                  aria-label="Portée"
                  hideSingleSelectionIndicator
                >
                  <mat-button-toggle value="CONTRACT_COMPANY">Société du contrat</mat-button-toggle>
                  <mat-button-toggle value="ALL_COMPANIES">Toutes les sociétés</mat-button-toggle>
                </mat-button-toggle-group>
              }
            </div>
          }
        </div>
        <div class="hint">Relations au contrat</div>
        @for (relation of relations; track relation) {
          <div class="relation">
            <mat-checkbox [formControl]="relationControl(relation)">{{
              relationLabels[relation]
            }}</mat-checkbox>
          </div>
        }
        @if (nobody) {
          <p class="nobody">
            Personne ne sera notifié par cette ligne : cochez au moins un profil ou une relation.
          </p>
        }
      </section>
    </div>

    @if (hasCadence) {
      <div class="column">
        <section class="cadence panel" formGroupName="cadence">
          <h3>Cadence</h3>
          <mat-form-field>
            <mat-label>Premier rappel après (jours ouvrés)</mat-label>
            <input
              matInput
              type="number"
              formControlName="firstReminderDelay"
              [min]="minDays"
              [max]="maxDays"
              step="1"
            />
            <mat-error>De {{ minDays }} à {{ maxDays }} jours ouvrés</mat-error>
          </mat-form-field>
          <div class="frequency">
            <span>Fréquence</span>
            <mat-button-toggle-group
              formControlName="frequency"
              aria-label="Fréquence"
              hideSingleSelectionIndicator
            >
              <mat-button-toggle value="FIXED">Fixe</mat-button-toggle>
              <mat-button-toggle value="DEGRESSIVE">Dégressive</mat-button-toggle>
            </mat-button-toggle-group>
          </div>
          @if (isFixed) {
            <mat-form-field>
              <mat-label>Puis tous les (jours ouvrés)</mat-label>
              <input
                matInput
                type="number"
                formControlName="frequencyInterval"
                [min]="minDays"
                [max]="maxDays"
                step="1"
              />
              <mat-error>De {{ minDays }} à {{ maxDays }} jours ouvrés</mat-error>
            </mat-form-field>
          } @else {
            <p class="hint">
              Les rappels s'espacent : 1, 2, 3, 5, 8, 13, 21… jours ouvrés entre deux envois.
            </p>
          }
          <mat-checkbox formControlName="limited">Limiter le nombre de rappels</mat-checkbox>
          @if (limited) {
            <mat-form-field>
              <mat-label>Nombre maximal de rappels</mat-label>
              <input matInput type="number" formControlName="maxReminders" min="1" step="1" />
              <mat-error>Au moins un rappel</mat-error>
            </mat-form-field>
          }
          <p class="hint">
            Aucun rappel ne part le samedi ni le dimanche ; les jours fériés comptent comme ouvrés.
          </p>
        </section>

        <section class="panel">
          <h3>Calendrier des rappels</h3>
          <p class="hint">
            Pour une surveillance qui s'ouvrirait aujourd'hui, {{ today | date: 'EEEE d MMMM' }}.
          </p>
          @if (schedule; as reminders) {
            <ol class="schedule">
              @for (reminder of reminders; track $index) {
                <li>
                  <span class="rank">R{{ $index + 1 }}</span>
                  <span>{{ reminder.date | date: 'EEE d MMM' }}</span>
                  <span class="interval">+{{ reminder.gap }} j</span>
                </li>
              }
            </ol>
            <p class="schedule-end">
              @if (limited) {
                Puis plus aucun rappel : le maximum est atteint.
              } @else {
                Et ainsi de suite, tant que la situation dure.
              }
            </p>
          } @else {
            <p class="hint">Corrigez la cadence pour voir le calendrier.</p>
          }
        </section>
      </div>
    }
  </div>
</mat-dialog-content>

<mat-dialog-actions>
  @if (line && !isGeneral) {
    <button matButton class="delete" type="button" (click)="delete()">
      {{ confirmingDelete ? 'Confirmer la suppression' : 'Supprimer la ligne' }}
    </button>
  }
  <span class="spacer"></span>
  <button matButton type="button" (click)="cancel()">Annuler</button>
  <button
    matButton="filled"
    class="save"
    type="button"
    [disabled]="form.invalid || saving"
    (click)="save()"
  >
    Enregistrer
  </button>
</mat-dialog-actions>
